import { Component } from '@angular/core';
import { Router } from '@angular/router';
import dxForm, { SimpleItem } from 'devextreme/ui/form';
import dxPopup, { Properties, ToolbarItem } from 'devextreme/ui/popup';
import dxScheduler from 'devextreme/ui/scheduler';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { BaseComponent } from 'src/app/core/base/base.component';
import { CommonDoctorViewModel } from 'src/app/model/common/doctor.view';
import { CommonPatientViewModel } from 'src/app/model/common/patient.view';
import { CommonTurnModel } from 'src/app/model/common/turn';
import { CommonTurnViewModel } from 'src/app/model/common/turn.view';
import { TurnDialogService } from '../common/turn/components/turn.dialog.service';
import { HOME_CONSTANTS } from './home.constants';

@Component({
  selector: 'ms-home',
  templateUrl: './home.component.html',
  providers: [DynamicDialogRef],
})
export class HomeViewComponent extends BaseComponent {
  readonly screen = HOME_CONSTANTS;
  private schedulerComponent?: dxScheduler;
  private isDeleting = false;
  protected setScreen = true;
  turns: CommonTurnViewModel[] = [];
  agendaView = 'month';
  isCounter = false;
  isVital = false;
  isDoctor = false;
  turnsResource: { id?: boolean; text?: string; color?: string }[] = [];
  turnsResourceColor: { id?: number; text?: string; color?: string }[] = [];
  isPatch = false;
  isPatchDoctor = false;
  isPatchStatus = false;
  turnId?: number;
  patientHelp?: CommonPatientViewModel;
  doctorHelp?: CommonDoctorViewModel;
  specialty?: string;
  specialtyCodeAlt?: string;
  specialtyName?: string;
  turnStart?: Date;
  turnEnd?: Date;

  constructor(protected service: TurnDialogService, protected router: Router) {
    super();
  }

  onInit = () => {
    this.service.setLoadingState();
    this.subscriptions.push(
      this.service.readAllTurns().subscribe((result) => {
        this.turns = result.map((item) => ({
          ...item,
          turnsResourceColor: item.id,
        }));
        this.turnsResourceColor = result.map((item) => ({
          id: item.id,
          text: item.turnBalance == null || item.turnBalance === 0 ? item.patientName : 'Saldo pendiente',
          color: item.turnBalance == null || item.turnBalance === 0 ? item.specialtyColor : '#aa0000',
        }));
      }),
      this.service.getUserInformation().subscribe((userInfo) => {
        if (userInfo.roles) {
          this.isCounter = userInfo.roles.isCounter ?? false;
          this.isVital = userInfo.roles.isVitalSign ?? false;
          this.isDoctor = userInfo.roles.isDoctor ?? false;
        }
      }),
      this.service.getFormSavedStore().subscribe(({ turn, formSaved }) => {
        if (formSaved) {
          this.schedulerComponent?.hideAppointmentPopup(false);
          this.service.setFormSaved({}, false);
          if (this.isPatch) {
            const turnIdx = this.turns.findIndex((item) => item.id === turn.id);
            this.turns[turnIdx] = turn;
          } else {
            this.schedulerComponent?.addAppointment(turn);
          }
        }
      })
    );
  };

  onAppointmentInitialized = ({
    component: scheduler,
  }: {
    component: dxScheduler;
  }) => {
    this.schedulerComponent = scheduler;
  };

  onAppointmentOpening = ($event: {
    appointmentData: CommonTurnViewModel;
    form: dxForm;
    popup: dxPopup;
    cancel: boolean;
  }) => {
    if (this.isDeleting || !this.isCounter) {
      $event.cancel = true;
      if (this.isDoctor) {
        this.router.navigate(['/consulta'], {
          queryParams: { turn: $event.appointmentData.id },
        });
      } else if (this.isVital) {
        this.router.navigate(['/signos-vitales'], {
          queryParams: $event.appointmentData,
          skipLocationChange: true,
        });
      }
      return;
    }

    const { appointmentData: turn, form, popup } = $event;

    this.turnStart = new Date(turn.turnDateTime!);
    this.turnEnd = new Date(this.turnStart.getTime() + 900000);

    if (turn.id) {
      this.isPatch = true;
      this.isPatchDoctor = true;
      this.turnId = turn.id;
      this.specialty = turn.specialty;
      this.specialtyCodeAlt = turn.specialtyCodeAlt;
      this.patientHelp = {
        id: turn.patient,
        fullName: turn.patientName,
        document: turn.patientDocument,
      };
      this.doctorHelp = {
        id: turn.doctor,
        fullName: turn.doctorName,
        document: turn.doctorDocument,
      };
    } else {
      this.isPatch = false;
      this.isPatchDoctor = false;
      this.turnId = undefined;
      this.specialty = undefined;
      this.specialtyCodeAlt = undefined;
      this.patientHelp = undefined;
      this.doctorHelp = undefined;
    }

    form.option('items', [
      {
        template: 'turnComponent',
        colSpan: 2,
      } as SimpleItem,
    ]);
    popup.option({
      dragEnabled: false,
      toolbarItems: [
        {
          location: 'before',
          text: this.isPatch
            ? this.screen.actions.edit
            : this.screen.actions.add,
          toolbar: 'top',
        } as ToolbarItem,
        {
          location: 'after',
          template: 'closeButton',
          toolbar: 'top',
        } as ToolbarItem,
      ],
    } as Properties);
    this.schedulerComponent?.hideAppointmentTooltip();
  };

  onAppointmentUpdating = ($event: any) => {
    this.isPatch = true;
    const dataToUpdate: CommonTurnViewModel = $event.newData;
    const turn: CommonTurnModel = {
      doctor: dataToUpdate.doctor,
      turnStart: dataToUpdate.turnDateTime,
      turnEnd: dataToUpdate.endTurnDateTime,
    };
    this.subscriptions.push(
      this.service.patchAgenda(dataToUpdate.id!, turn).subscribe()
    );
  };

  onAppointmentDeleting = ($event: any) => {
    const turn: CommonTurnViewModel = $event.appointmentData;
    this.subscriptions.push(
      this.service
        .deleteAgenda(turn.id!)
        .subscribe(() => (this.isDeleting = false))
    );
  };

  closeAppointmentPopup = () =>
    this.schedulerComponent?.hideAppointmentPopup(false);

  deleteAppointment = (appointmentData: CommonTurnViewModel) => {
    this.schedulerComponent?.deleteAppointment(appointmentData);
    this.schedulerComponent?.hideAppointmentTooltip();
    this.isDeleting = true;
  };

  goToConsult = (turn: CommonTurnViewModel, goToVital: boolean = false) => {
    if (goToVital) {
      this.router.navigate(['/signos-vitales'], {
        queryParams: turn,
        skipLocationChange: true,
      });
    } else {
      this.router.navigate(['/consulta'], {
        queryParams: { turn: turn.id },
      });
    }
  };
}
